import React, { Fragment } from 'react';
import { List } from 'immutable';
import { twMerge } from 'tailwind-merge';
import {
    Listbox,
    ListboxButton,
    ListboxOption,
    ListboxOptions,
    Transition,
} from '@headlessui/react';
import { setSelectedCtiContact } from 'flow/actions/cti-actions';
import Icon from 'flow/components/ui-components/Icon';
import { useAppDispatch } from 'flow/hooks/useRedux';
import { ContactKey } from 'flow/types/EntityKeys';
import { TypedMap } from 'flow/types/TypedMap';

interface Contact {
    key: ContactKey;
    firstName: string;
}

interface Props {
    selectedCtiContact: TypedMap<Contact>;
    contactsMatchingNumber: List<TypedMap<Contact>>;
}

const IntegrationContactSelector = ({
    selectedCtiContact,
    contactsMatchingNumber,
}: Props) => {
    const dispatch = useAppDispatch();

    const handleChange = (contact: TypedMap<Contact>) => {
        dispatch(setSelectedCtiContact(contact));
    };

    return (
        <Listbox
            value={selectedCtiContact}
            onChange={handleChange}
            by={(a, b) => a?.get('key') === b?.get('key')}
        >
            <div className="relative w-full">
                <ListboxButton className="focus-ring flex h-7.5 w-full items-center justify-between rounded border border-gray-500 bg-transparent px-3 text-left text-xs text-white">
                    <span>{selectedCtiContact?.get('firstName') ?? ''}</span>
                    <Icon
                        iconKey="arrow_down"
                        className="size-3 text-gray-400"
                    />
                </ListboxButton>
                <Transition
                    as={Fragment}
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <ListboxOptions
                        anchor="bottom"
                        className="z-50 max-h-40 w-(--button-width) overflow-auto rounded bg-gray-800 p-0 shadow-lg focus:outline-none"
                    >
                        {contactsMatchingNumber.toArray().map((contact) => (
                            <ListboxOption
                                key={contact.get('key')}
                                data-test-id="cti-widget-contact-selector"
                                value={contact}
                                className={({ focus }) =>
                                    twMerge(
                                        'cursor-default px-3 py-1 text-xs text-white select-none',
                                        focus ? 'bg-gray-500' : 'bg-gray-800',
                                    )
                                }
                            >
                                {contact.get('firstName')}
                            </ListboxOption>
                        ))}
                    </ListboxOptions>
                </Transition>
            </div>
        </Listbox>
    );
};

export default React.memo(IntegrationContactSelector);
